import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import {
  pushDataLayer,
  buildCustomer,
  readPendingPurchase,
  clearPendingPurchase,
} from "@/lib/gtm";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking | success | failed | processing
  const purchaseFired = useRef(false);

  useEffect(() => {
    const redirectStatus = params.get("redirect_status");
    // If Stripe redirected here after 3DS, `redirect_status` will be present.
    // If we navigated here manually after inline confirmation, no query params → assume success.
    let next;
    if (!redirectStatus) next = "success";
    else if (redirectStatus === "succeeded") next = "success";
    else if (redirectStatus === "processing") next = "processing";
    else next = "failed";
    setStatus(next);

    // ── GA4 purchase_ff ─────────────────────────────────────────
    // Fire exactly once on confirmed success. We prefer Stripe's `payment_intent`
    // from the redirect URL for the transaction_id, and fall back to the pi id
    // captured at begin_checkout time.
    if (next === "success" && !purchaseFired.current) {
      purchaseFired.current = true;
      const snap = readPendingPurchase();
      if (snap) {
        const transaction_id =
          params.get("payment_intent") || snap.transaction_id || undefined;
        pushDataLayer("purchase_ff", {
          ecommerce: {
            transaction_id,
            currency: snap.currency,
            value: snap.value,
            coupon: snap.coupon || undefined,
            items: snap.item ? [snap.item] : [],
          },
          funnel: snap.funnel,
          customer: buildCustomer(snap.contact || {}),
        });
        clearPendingPurchase();
      }
    }
  }, [params]);

  if (status === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="max-w-md w-full text-center space-y-5 bg-card p-8 rounded-2xl border">
          <div className="h-16 w-16 rounded-full bg-destructive/10 text-destructive grid place-items-center mx-auto">
            <XCircle className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-display font-semibold">لم يكتمل الدفع</h1>
          <p className="text-muted-foreground">
            تعذر إتمام عملية الدفع. قد يكون السبب رفض البنك أو فشل التحقق. يرجى المحاولة ببطاقة أخرى أو التواصل مع بنكك.
          </p>
          <Link to="/checkout"><Button className="rounded-full">المحاولة مرة أخرى</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="max-w-md w-full text-center space-y-5 bg-card p-8 rounded-2xl border">
        <div className="h-16 w-16 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-display font-semibold">
          {status === "processing" ? "الدفع قيد المعالجة" : "تم استلام الدفع!"}
        </h1>
        <p className="text-muted-foreground">
          ستستلم رسالة على بريدك الإلكتروني خلال دقائق تحتوي على رابط تفعيل حسابك.
          افتحه لتحديد كلمة مرورك والدخول إلى دوراتك.
        </p>
        <Link to="/login"><Button variant="outline" className="rounded-full">تسجيل الدخول</Button></Link>
      </div>
    </div>
  );
}
